import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getCanoniMarketplace, deleteCanoneMarketplace, deleteMultipleCanoniMarketplace } from '../services/api';
import { Table, Form, Spinner, Alert, Button, Row, Col, Dropdown, Pagination, Card, Badge } from 'react-bootstrap';
import { useMediaQuery } from 'react-responsive';
import MobileListLayout from '../common/MobileListLayout';
import { Link } from 'react-router-dom';
import ActionToolbar from '../common/ActionToolbar';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import SortController from '../common/SortController';
import ColumnOrderController from '../common/ColumnOrderController';
import FilterController from '../common/FilterController';
import FilterModal from '../common/FilterModal';

const columnHelper = createColumnHelper();

const allColumnsMap = {
    id: "ID",
    denominazione: "Denominazione",
    percentuale: "Percentuale (%)",
    costo_fisso: "Costo Fisso (€)",
};

const initialVisibleColumns = ['denominazione', 'percentuale', 'costo_fisso'];

const visibilityKey = 'table-visibility-canoni-marketplace';

function CanoniMarketplaceList() {
  const isMobile = useMediaQuery({ maxWidth: 991 });
  const [canoni, setCanoni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCanoneIds, setSelectedCanoneIds] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [columnsToFilter, setColumnsToFilter] = useState([]);

  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem(visibilityKey);
    if (saved) return JSON.parse(saved);
    return Object.keys(allColumnsMap).reduce((acc, key) => {
      acc[key] = initialVisibleColumns.includes(key);
      return acc;
    }, {});
  });
  
  const [sorting, setSorting] = useState(() => {
    const saved = sessionStorage.getItem('list-sorting-canoni-marketplace');
    return saved ? JSON.parse(saved) : [];
  });

  const [columnFilters, setColumnFilters] = useState(() => {
    const saved = sessionStorage.getItem('list-filters-canoni-marketplace');
    return saved ? JSON.parse(saved) : [];
  });

  const [columnOrder, setColumnOrder] = useState(() => {
    const savedOrder = localStorage.getItem('table-column-order-canoni-marketplace');
    const defaultOrder = ['select', ...Object.keys(allColumnsMap)];
    if (savedOrder) {
        let parsedOrder = JSON.parse(savedOrder);
        parsedOrder = parsedOrder.filter(c => c !== 'select');
        return ['select', ...parsedOrder];
    }
    return defaultOrder;
  });

  const [columnSizing, setColumnSizing] = useState(() => {
    const saved = localStorage.getItem('table-column-sizing-canoni-marketplace');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    sessionStorage.setItem('list-sorting-canoni-marketplace', JSON.stringify(sorting));
  }, [sorting]);

  useEffect(() => {
    sessionStorage.setItem('list-filters-canoni-marketplace', JSON.stringify(columnFilters));
  }, [columnFilters]);

  useEffect(() => {
    localStorage.setItem('table-column-order-canoni-marketplace', JSON.stringify(columnOrder));
  }, [columnOrder]);

  useEffect(() => {
    localStorage.setItem('table-column-sizing-canoni-marketplace', JSON.stringify(columnSizing));
  }, [columnSizing]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [totalCanoni, setTotalCanoni] = useState(0);

  const fetchCanoniData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = JSON.stringify(
        columnFilters.reduce((acc, filter) => {
          const { id, value } = filter;
          if (Array.isArray(value) && value.length > 0) acc[id] = { 'in': value };
          else if (typeof value === 'string' && value.toUpperCase() === 'N/A') acc[id] = null;
          else acc[id] = value;
          return acc;
        }, {})
      );
      const sortParams = sorting.map(sort => `${sort.id}:${sort.desc ? 'desc' : 'asc'}`);
      const response = await getCanoniMarketplace((currentPage - 1) * itemsPerPage, itemsPerPage, sortParams, filters);
      setCanoni(response.data.canoni_marketplace || []);
      setTotalCanoni(response.data.total || 0);
    } catch (err) {
      setError('Errore nel caricamento dei canoni marketplace.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, sorting, columnFilters]);

  // Helper functions for mobile view
  const formatCurrency = (amount) => {
    if (!amount) return '€0,00';
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(parseFloat(amount));
  };

  const formatPercentage = (percentage) => {
    if (percentage === null || percentage === undefined) return 'N/D';
    return `${percentage}%`;
  };

  const renderCanoneCard = useCallback((canone) => {
    return (
      <Card className="mb-2" style={{ fontSize: '0.9rem' }}>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-2">
            <strong style={{ fontSize: '1rem' }}>{canone.denominazione}</strong>
          </div>

          <Row className="mb-2">
            <Col xs={6}>
              <small className="text-muted">Percentuale:</small>
              <div><strong>{formatPercentage(canone.percentuale)}</strong></div>
            </Col>
            <Col xs={6}>
              <small className="text-muted">Costo Fisso:</small>
              <div><strong>{formatCurrency(canone.costo_fisso)}</strong></div>
            </Col>
          </Row>

          <div className="d-flex gap-2 mt-3">
            <Link to={`/canoni-marketplace/${canone.id}/edit`}>
              <Button variant="primary" size="sm">Modifica</Button>
            </Link>
            <Button
              variant="danger"
              size="sm"
              onClick={async () => {
                if (window.confirm(`Eliminare il canone "${canone.denominazione}"?`)) {
                  try {
                    await deleteCanoneMarketplace(canone.id);
                    fetchCanoniData();
                  } catch (err) {
                    alert('Errore durante l\'eliminazione');
                  }
                }
              }}
            >
              Elimina
            </Button>
          </div>
        </Card.Body>
      </Card>
    );
  }, [fetchCanoniData]);

  const actionsDropdownContent = useMemo(() => (
    <>
      <ActionToolbar
        selectedItemIds={selectedCanoneIds}
        entityType="canoni-marketplace"
        onDeleteSuccess={fetchCanoniData}
        deleteApiMap={{ 'canoni-marketplace': deleteCanoneMarketplace }}
        deleteMultipleApiMap={{ 'canoni-marketplace': deleteMultipleCanoniMarketplace }}
      />
    </>
  ), [selectedCanoneIds, fetchCanoniData]);

  useEffect(() => {
    fetchCanoniData();
  }, [fetchCanoniData]);

  const getColumnValue = (canone, columnName) => {
    const val = canone[columnName];
    if (columnName === 'percentuale') return `${val}%`;
    if (columnName === 'costo_fisso') return `€ ${parseFloat(val).toFixed(2)}`;
    return val != null ? String(val) : 'N/A';
  };

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => <Form.Check type="checkbox" onChange={table.getToggleAllRowsSelectedHandler()} checked={table.getIsAllRowsSelected()} disabled={canoni.length === 0} />,
        cell: ({ row }) => <Form.Check type="checkbox" checked={row.getIsSelected()} onChange={row.getToggleSelectedHandler()} onClick={(e) => e.stopPropagation()} />,
        minSize: 30, maxSize: 30, size: 30, enableResizing: false, enableSorting: false, enableColumnFilter: false,
      }),
      ...Object.entries(allColumnsMap).map(([key, value]) =>
        columnHelper.accessor(key, {
          header: value,
          cell: info => getColumnValue(info.row.original, key),
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
        })
      ),
    ],
    [canoni]
  );

  const onColumnVisibilityChange = (updater) => {
    const newState = typeof updater === 'function' ? updater(columnVisibility) : updater;
    setColumnVisibility(newState);
    localStorage.setItem(visibilityKey, JSON.stringify(newState));
  };

  const table = useReactTable({
    data: canoni, columns, state: { columnVisibility, sorting, columnFilters, columnOrder, columnSizing },
    onColumnVisibilityChange, onSortingChange: setSorting, onColumnFiltersChange: setColumnFilters, onColumnOrderChange: setColumnOrder, onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel(), getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(), getFacetedUniqueValues: getFacetedUniqueValues(),
    enableColumnResizing: true, columnResizeMode: 'onChange', enableColumnReordering: true, manualFiltering: true,
  });

  const handleAutoResize = (column) => {
    const headerText = String(column.columnDef.header);
    const cells = table.getRowModel().rows.map(row => String(row.original[column.id] ?? ''));
    const charMultiplier = 8, padding = 30;
    const headerWidth = headerText.length * charMultiplier;
    const maxWidth = cells.reduce((max, cell) => Math.max(max, cell.length * charMultiplier), headerWidth);
    const newSize = Math.ceil(maxWidth) + padding;
    table.setColumnSizing(prev => ({ ...prev, [column.id]: newSize }));
  };

  const { rows } = table.getSelectedRowModel();
  useEffect(() => {
    const newSelectedIds = rows
      .map((row) => row.original.id)
      .filter((id) => id != null);
    setSelectedCanoneIds(newSelectedIds);
  }, [rows]);

  const handleRowClick = (row) => row.toggleSelected();

  const handleResetFilters = () => {
    table.resetColumnFilters();
    setSorting([]);
  };

  const deleteApiMap = { canoni_marketplace: deleteCanoneMarketplace };
  const deleteMultipleApiMap = { canoni_marketplace: deleteMultipleCanoniMarketplace };

  const renderPaginationItems = () => {
    const totalPages = Math.ceil(totalCanoni / itemsPerPage);
    const pageNumbers = []; const maxPagesToShow = 5; let startPage, endPage;
    if (totalPages <= maxPagesToShow) { startPage = 1; endPage = totalPages; }
    else {
      if (currentPage <= Math.ceil(maxPagesToShow / 2)) { startPage = 1; endPage = maxPagesToShow; }
      else if (currentPage + Math.floor(maxPagesToShow / 2) >= totalPages) { startPage = totalPages - maxPagesToShow + 1; endPage = totalPages; }
      else { startPage = currentPage - Math.floor(maxPagesToShow / 2); endPage = currentPage + Math.floor(maxPagesToShow / 2); }
    }
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(<Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>{i}</Pagination.Item>);
    }
    return pageNumbers;
  };

  const HeaderComponent = ({ header }) => {
    const { column } = header;
    const isSelectColumn = column.id === 'select';
    return (
      <th style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#f8f9fa', width: `${column.getSize()}px` }}>
        <div className="d-flex align-items-center justify-content-center p-2 h-100" style={{ width: '100%', overflow: 'hidden' }}>
          <span className="fw-bold text-center" style={{ whiteSpace: 'normal', flexGrow: 1 }}>{flexRender(column.columnDef.header, header.getContext())}</span>
        </div>
        {header.column.getCanResize() && !isSelectColumn && (
          <div onMouseDown={header.getResizeHandler()} onTouchStart={header.getResizeHandler()} onDoubleClick={() => handleAutoResize(header.column)} className={`resizer ${header.column.getIsResizing() ? 'isResizing' : ''}`} style={{ transform: header.column.getIsResizing() ? `translateX(${table.getState().columnSizingInfo.deltaOffset}px)` : '' }} />
        )}
      </th>
    );
  };
  
  const hasManualFilters = table.getState().columnFilters.length > 0;

  return (
    <>
      <Row className="mb-4 align-items-center">
        <Col className="d-flex justify-content-center align-items-baseline">
          <h1 className="h3 mb-0">Canoni Marketplace</h1>
          <span className="h3 mb-0 mx-2 text-muted"> - </span>
          <span className="h3 mb-0 text-muted">Totale: {totalCanoni}</span>
        </Col>
      </Row>
    <Card>
      <Card.Header className="sticky-card-header">
        <Row className="align-items-center">
          <Col md={6} className="d-flex justify-content-start align-items-center">
            <Button as={Link} to="/canoni-marketplace/new" className="me-2" size="sm" variant="success">Nuovo Canone</Button>
          </Col>
          <Col md={6} className="d-flex justify-content-end align-items-center">
            <Dropdown className="me-2" autoClose="outside">
              <Dropdown.Toggle variant="outline-secondary" id="dropdown-filters" size="sm">Filtri
                {columnFilters.length > 0 && (<Badge pill bg="primary" className="ms-2">{columnFilters.length}</Badge>)}
              </Dropdown.Toggle>
              <Dropdown.Menu className="long-dropdown-menu">
                <FilterController table={table} allColumnsMap={allColumnsMap} columnsToFilter={columnsToFilter} setColumnsToFilter={setColumnsToFilter} openFilterModal={() => setShowFilterModal(true)} />
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown className="me-2" autoClose="outside">
              <Dropdown.Toggle variant="outline-secondary" id="dropdown-sorting" size="sm">Ordinamento</Dropdown.Toggle>
              <Dropdown.Menu className="long-dropdown-menu p-2">
                <SortController columns={table.getAllLeafColumns()} sorting={sorting} setSorting={setSorting} />
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown className="me-2" autoClose="outside">
              <Dropdown.Toggle variant="outline-secondary" id="dropdown-columns" size="sm">Colonne</Dropdown.Toggle>
              <Dropdown.Menu className="long-dropdown-menu p-0" style={{ minWidth: '500px' }}>
                <div className="d-flex">
                  <div className="p-2" style={{ flex: 1, borderRight: '1px solid #dee2e6' }}><ColumnOrderController table={table} allColumnsMap={allColumnsMap} /></div>
                  <div className="p-2" style={{ flex: 1 }}>
                    <h6 className="fw-bold">Mostra Colonne</h6>
                    <div className="d-flex flex-column">
                      {table.getAllColumns().filter(c => c.id !== 'select').map(c => <Form.Check key={c.id} type="checkbox" label={allColumnsMap[c.id]} checked={c.getIsVisible()} onChange={c.getToggleVisibilityHandler()} className="dropdown-item" />)}
                    </div>
                  </div>
                </div>
              </Dropdown.Menu>
            </Dropdown>
            <Button variant="outline-secondary" size="sm" className="ms-2" onClick={handleResetFilters} disabled={!hasManualFilters && sorting.length === 0}>Azzera Filtri</Button>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body>
        {loading && <div className="text-center"><Spinner animation="border" /></div>}
        {error && <Alert variant="danger">{error}</Alert>}
        
        {!loading && !error && (
          <>
            {isMobile ? (
              <MobileListLayout
                items={canoni}
                renderCard={renderCanoneCard}
                entityName="canone marketplace"
                entityNamePlural="canoni marketplace"
                createPath="/canoni-marketplace/new"
                createButtonText="Nuovo Canone"
                selectedIds={selectedCanoneIds}
                onToggleSelect={(canoneId) => {
                  const row = table.getRowModel().rows.find(r => r.original.id === canoneId);
                  if (row) row.toggleSelected();
                }}
                actionsDropdownContent={actionsDropdownContent}
                showSelectAll={true}
                onSelectAll={() => table.toggleAllRowsSelected()}
                isAllSelected={table.getIsAllRowsSelected()}
              />
            ) : (
              <div style={{ maxHeight: 'calc(100vh - 350px)', overflow: 'auto' }}>
                <Table striped bordered hover style={{ width: table.getTotalSize() }} className="aligned-table">
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => <HeaderComponent key={header.id} header={header} />)}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => (
                  <tr key={row.id} onClick={() => handleRowClick(row)} className={row.getIsSelected() ? 'table-primary' : ''} style={{ cursor: 'pointer', height: '40px' }}>
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} style={{ verticalAlign: 'middle', width: `${cell.column.getSize()}px`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
              )}
            </>
        )}
        {!isMobile && (
          <ActionToolbar selectedItemIds={selectedCanoneIds} entityType="canoni-marketplace" onDeleteSuccess={fetchCanoniData} deleteApiMap={deleteApiMap} deleteMultipleApiMap={deleteMultipleApiMap} showEdit={true} />
        )}
      </Card.Body>
      <Card.Footer className="d-flex justify-content-between align-items-center">
        <Form.Group as={Row} className="align-items-center mb-0">
          <Col xs="auto"><Form.Label className="mb-0">Record per pagina</Form.Label></Col>
          <Col xs="auto">
            <Form.Select size="sm" value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
              <option value={10}>10</option><option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
            </Form.Select>
          </Col>
        </Form.Group>
        <Pagination size="sm" className="mb-0">
          <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
          <Pagination.Prev onClick={() => setCurrentPage(prev => prev - 1)} disabled={currentPage === 1} />
          {renderPaginationItems()}
          <Pagination.Next onClick={() => setCurrentPage(prev => prev + 1)} disabled={currentPage === Math.ceil(totalCanoni / itemsPerPage)} />
          <Pagination.Last onClick={() => setCurrentPage(Math.ceil(totalCanoni / itemsPerPage))} disabled={currentPage === Math.ceil(totalCanoni / itemsPerPage)} />
        </Pagination>
      </Card.Footer>
    </Card>
    <FilterModal show={showFilterModal} handleClose={() => setShowFilterModal(false)} table={table} columnsToFilter={columnsToFilter} />
  </>
  );
}

export default CanoniMarketplaceList;